import { cn } from "@artisan/ui";
import { Ruler, Zap } from "lucide-react";
import { useId, useState } from "react";

import type { M } from "../project/model";
import type { Source } from "../survey/types";
import { parseMm, umToMmText } from "../survey/units";

export interface MeasureInputProps {
  label: string;
  value: M;
  onChange: (m: M) => void;
  /** default source for a new value */
  source?: Source;
  hint?: string;
  showSource?: boolean;
  className?: string;
  invalid?: boolean;
}

/** A production length in millimetres, with how it was measured (laser or tape). */
export function MeasureInput({ label, value, onChange, source = "laser", hint, showSource = true, className, invalid }: MeasureInputProps) {
  const id = useId();
  const [text, setText] = useState(value ? umToMmText(value.um) : "");
  const [bad, setBad] = useState(false);
  // A new value from outside (another reading, an undo) replaces what is typed.
  const [shown, setShown] = useState(value?.um);
  if (shown !== value?.um) {
    setShown(value?.um);
    setText(value ? umToMmText(value.um) : "");
    setBad(false);
  }
  const src = value?.source ?? source;

  function commit() {
    if (!text.trim()) {
      setBad(false);
      if (value) onChange(null);
      return;
    }
    const um = parseMm(text);
    if (um === null) {
      setBad(true);
      return;
    }
    setBad(false);
    if (!value || value.um !== um) onChange({ um, source: src });
  }

  return (
    <div className={cn("@container grid min-w-0 gap-1", className)}>
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <div
        className={cn(
          "flex h-12 items-center rounded-lg border bg-card pl-3 shadow-xs focus-within:shadow-ring",
          bad || invalid ? "border-destructive" : value ? "border-border" : "border-dashed border-input",
        )}
      >
        <input
          id={id}
          inputMode="decimal"
          enterKeyHint="next"
          autoComplete="off"
          className="w-0 min-w-0 flex-1 bg-transparent font-mono text-[17px] tabular outline-none placeholder:text-muted-foreground/60"
          placeholder="0"
          value={text}
          aria-invalid={bad || invalid || undefined}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
        />
        <span className="pr-2 pl-1 text-xs text-muted-foreground">мм</span>
        {showSource ? (
          <button
            type="button"
            className={cn(
              "mr-1 flex h-9 items-center gap-1 rounded-md px-2 text-xs font-medium",
              src === "laser" ? "bg-primary-soft text-primary" : "bg-secondary text-secondary-foreground",
            )}
            aria-label={`Хэмжсэн арга: ${src === "laser" ? "лазер" : "метр"}. Солих`}
            onClick={() => value && onChange({ um: value.um, source: src === "laser" ? "tape" : "laser" })}
            disabled={!value}
          >
            {src === "laser" ? <Zap className="size-3.5" aria-hidden /> : <Ruler className="size-3.5" aria-hidden />}
            {/* In narrow fields (two per row) the icon alone says it; the label has the word. */}
            <span className="hidden @[15rem]:inline">{src === "laser" ? "Лазер" : "Метр"}</span>
          </button>
        ) : null}
      </div>
      {bad ? <p className="text-xs text-destructive">Миллиметрээр оруулна уу, жишээ нь 2450 эсвэл 2450.5</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** A design value in millimetres (zones, partitions): no measuring source. */
export function MmInput({ label, value, onChange, className }: { label: string; value: number; onChange: (um: number) => void; className?: string }) {
  return (
    <MeasureInput
      label={label}
      value={{ um: value, source: "laser" }}
      onChange={(m) => onChange(m?.um ?? 0)}
      showSource={false}
      className={className}
    />
  );
}
